import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import "./ReportsView.scss";
import {
  Laptop,
  Clock,
  HandHeart,
  Check,
  Lightbulb,
  Search,
  ChevronDown,
} from "lucide-react";
import MetricCard from "../../../../widgets/MetricCard/MetricCard";
import { useEffect, useState } from "react";
import DataTable from "../../../../widgets/DataTable";
import ideasServices from "../../../../services/ideasServices";

export default function ReportsView() {
  const [usage, setUsage] = useState("");
  const [followupMonthValue, setFollowupMonthValue] = useState(0);
  const [followupYearValue, setFollowupYearValue] = useState(0);
  const [followup, setFollowup] = useState("");
  const [responseMonthValue, setResponseMonthValue] = useState(0);
  const [responseYearValue, setResponseYearValue] = useState(0);
  const [responsetime, setResponsetime] = useState("");
  const [approvalYearValue, setApprovalYearValue] = useState(0);
  const [approval, setApproval] = useState("");
  const [ideasCount, setIdeasCount] = useState([]);
  const [ideasCountMonthValue, setIdeasCountMonthValue] = useState<number>(0);
  const [ideasCountYearValue, setIdeasCountYearValue] = useState<number>(0);
  const [ideasYears, setIdeasYears] = useState([]);
  const [searchInput, setSearchInput] = useState<string>();
  const [unitNames, setUnitNames] = useState<Array<any>>([]);
  const [visibleCount, setVisibleCount] = useState<Array<any>>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingResponse, setIsLoadingResponse] = useState(true);
  const [isLoadingFollowup, setIsLoadingFollowup] = useState(true);
  const [isLoadingApproval, setIsLoadingApproval] = useState(true);

  const metrics = [
    {
      id: "usage",
      name: "Tasa de Uso",
      hasMonthFilter: false,
      hasYearFilter: false,
      icon: <Laptop />,
      value: usage,
      isLoading: isLoadingMetrics,
    },
    {
      id: "response",
      name: "Tiempo de Atención",
      hasMonthFilter: true,
      hasYearFilter: true,
      icon: <Clock />,
      monthValue: responseMonthValue,
      setMonthValue: setResponseMonthValue,
      yearValue: responseYearValue,
      setYearValue: setResponseYearValue,
      value: responsetime,
      yearsData: ideasYears,
      isLoading: isLoadingResponse,
    },
    {
      id: "followup",
      name: "Tasa de Seguimiento",
      hasMonthFilter: true,
      hasYearFilter: true,
      icon: <HandHeart />,
      monthValue: followupMonthValue,
      setMonthValue: setFollowupMonthValue,
      yearValue: followupYearValue,
      setYearValue: setFollowupYearValue,
      value: followup,
      yearsData: ideasYears,
      isLoading: isLoadingFollowup,
    },
    {
      id: "approval",
      name: "Tasa de Aprobación",
      hasMonthFilter: false,
      hasYearFilter: true,
      icon: <Check />,
      yearValue: approvalYearValue,
      setYearValue: setApprovalYearValue,
      value: approval,
      yearsData: ideasYears,
      isLoading: isLoadingApproval,
    },
  ];

  const ideasCountHeaders = [
    {
      name: "Unidad",
      id: "hospitalUnit",
    },
    {
      name: "# de Ideas",
      id: "unitIdeaCount",
    },
  ];

  const monthArray = Array.from({ length: 12 }, (_, i) => ({
    id: i + 1,
    name: capitalize(new Date(0, i).toLocaleString("es-ES", { month: "long" })),
  }));

  function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  useEffect(() => {
    const fetchData = async () => {
      await ideasServices
        .getUnitIdeaCount()
        .then((res) => {
          setIdeasCount(res);
          setVisibleCount(res);
        })
        .finally(() => setIsLoadingData(false));
      await ideasServices
        .getUnitIdeaResponseTime()
        .then((res) => calculateResponseTime(res))
        .finally(() => setIsLoadingResponse(false));
      await ideasServices
        .getUnitIdeaFollowup()
        .then((res) => {
          const percentageFollowup = percentageCalculations(
            res[0].changedIdeas,
            res[0].createdIdeas
          );
          setFollowup(percentageFollowup);
        })
        .finally(() => setIsLoadingFollowup(false));
      await ideasServices
        .getUnitIdeaApproval()
        .then((res) => {
          const percentageApproval = percentageCalculations(
            res[0].plannedIdeas,
            res[0].reviewedIdeas
          );
          setApproval(percentageApproval);
        })
        .finally(() => setIsLoadingApproval(false));
      await ideasServices.getAppUsage().then((res) => {
        const percentageUsage = percentageCalculations(
          res[0].userLogsCount,
          res[0].usersCount
        );
        setUsage(percentageUsage);
      });
      setIsLoadingMetrics(false);
      await ideasServices.getIdeasDates().then((res) => {
        const data = res[0].ideasYears;
        const yearsData = data.map((year: string) => {
          return {
            id: year,
            name: year.toString(),
          };
        });
        setIdeasYears(yearsData);
      });
    };
    fetchData();
  }, []);

  function calculateResponseTime(data: any[]) {
    const responseTimeArray = data.map((response) => {
      const responseCreatedAt: number = new Date(
        response.responseCreatedAt
      ).getTime();
      const ideaCreatedAt: number = new Date(response.ideaCreatedAt).getTime();
      const difference: number =
        (responseCreatedAt - ideaCreatedAt) / (1000 * 60 * 60 * 24);
      return difference;
    });
    const responseTimeMean =
      responseTimeArray.length > 0 ? getMean(responseTimeArray) : 0;
    responseTimeMean > 0
      ? setResponsetime(`${responseTimeMean} días`)
      : setResponsetime("N/A");
  }

  function getMean(timeArray: number[]) {
    if (timeArray.length === 0) {
      return 0;
    }

    const sum = timeArray.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0
    );
    const mean = sum / timeArray.length;
    return Math.round(mean);
  }

  function percentageCalculations(value1: number, value2: number): string {
    const percentageValue = value2 !== 0 ? (value1 / value2) * 100 : 0;
    const resultText =
      percentageValue !== 0 ? `${percentageValue.toFixed(2)}%` : "N/A";
    return resultText;
  }

  function formatDateToString(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  useEffect(() => {
    setIsLoadingFollowup(true);
    const fetchData = async () => {
      await ideasServices
        .getUnitIdeaFollowup()
        .then((res) => {
          const percentageFollowup = percentageCalculations(
            res[0].changedIdeas,
            res[0].createdIdeas
          );
          setFollowup(percentageFollowup);
        })
        .finally(() => setIsLoadingFollowup(false));
    };
    const fetchDataRange = async (startDate: string, endDate: string) => {
      await ideasServices
        .getUnitIdeaFollowupRange(startDate, endDate)
        .then((res) => {
          const percentageFollowup = percentageCalculations(
            res[0].changedIdeas,
            res[0].createdIdeas
          );
          setFollowup(percentageFollowup);
        })
        .finally(() => setIsLoadingFollowup(false));
    };

    const rangeDates = calculateDates(followupMonthValue, followupYearValue);
    const { startDate, endDate } = rangeDates;

    if (startDate && endDate) {
      fetchDataRange(startDate, endDate);
    } else {
      fetchData();
    }
  }, [followupMonthValue, followupYearValue]);

  useEffect(() => {
    setIsLoadingResponse(true);
    const fetchData = async () => {
      await ideasServices
        .getUnitIdeaResponseTime()
        .then((res) => {
          calculateResponseTime(res);
        })
        .finally(() => setIsLoadingResponse(false));
    };
    const fetchDataRange = async (startDate: string, endDate: string) => {
      await ideasServices
        .getUnitIdeaResponseTimeRange(startDate, endDate)
        .then((res) => {
          calculateResponseTime(res);
        })
        .finally(() => setIsLoadingResponse(false));
    };

    const rangeDates = calculateDates(responseMonthValue, responseYearValue);
    const { startDate, endDate } = rangeDates;

    if (startDate && endDate) {
      fetchDataRange(startDate, endDate);
    } else {
      fetchData();
    }
  }, [responseMonthValue, responseYearValue]);

  useEffect(() => {
    setIsLoadingApproval(true);
    const fetchData = async () => {
      await ideasServices
        .getUnitIdeaApproval()
        .then((res) => {
          const percentageApproval = percentageCalculations(
            res[0].plannedIdeas,
            res[0].reviewedIdeas
          );
          setApproval(percentageApproval);
        })
        .finally(() => setIsLoadingApproval(false));
    };
    const fetchDataRange = async (startDate: string, endDate: string) => {
      await ideasServices
        .getUnitIdeaApprovalRange(startDate, endDate)
        .then((res) => {
          const percentageApproval = res.createdIdeas
            ? percentageCalculations(
                res[0].plannedIdeas,
                res[0].createdIdeas + res[0].reviewedIdeas
              )
            : percentageCalculations(res[0].plannedIdeas, res[0].createdIdeas);
          setApproval(percentageApproval);
        })
        .finally(() => setIsLoadingApproval(false));
    };

    const rangeDates = calculateDates(0, approvalYearValue);
    const { startDate, endDate } = rangeDates;

    if (startDate && endDate) {
      fetchDataRange(startDate, endDate);
    } else {
      fetchData();
    }
  }, [approvalYearValue]);

  useEffect(() => {
    setIsLoadingData(true);
    const fetchDataRange = async (startDate: string, endDate: string) => {
      await ideasServices
        .getUnitIdeaCountRange(startDate, endDate)
        .then((res) => {
          setIdeasCount(res);
          setVisibleCount(res);
        })
        .finally(() => setIsLoadingData(false));
    };
    const fetchData = async () => {
      await ideasServices
        .getUnitIdeaCount()
        .then((res) => {
          setIdeasCount(res);
          setVisibleCount(res);
        })
        .finally(() => setIsLoadingData(false));
    };
    const rangeDates = calculateDates(
      ideasCountMonthValue,
      ideasCountYearValue
    );
    const { startDate, endDate } = rangeDates;

    if (startDate && endDate) {
      fetchDataRange(startDate, endDate);
    } else {
      fetchData();
    }
  }, [ideasCountMonthValue, ideasCountYearValue]);

  useEffect(() => {
    if (ideasCount.length > 0) {
      const unitNames = ideasCount.map((count: any) => count.hospitalUnit);
      setUnitNames(unitNames);
    }
  }, [ideasCount]);

  useEffect(() => {
    setIsLoadingData(false);
    if (searchInput) {
      const filteredCount = ideasCount.filter(
        (idea: any) => idea.hospitalUnit === searchInput
      );
      setVisibleCount(filteredCount);
    } else {
      setVisibleCount(ideasCount);
    }
  }, [searchInput]);

  useEffect(() => {
    visibleCount.length !== 0 && setIsLoadingData(false);
  }, [visibleCount]);

  const calculateDates = (monthValue: number, yearValue: number) => {
    let startDate: string;
    let endDate: string;

    if (monthValue === 0 && yearValue !== 0) {
      startDate = formatDateToString(new Date(yearValue, 0, 1));
      endDate = formatDateToString(new Date(yearValue, 11, 31));
    } else if (monthValue !== 0 && yearValue !== 0) {
      startDate = formatDateToString(new Date(yearValue, monthValue - 1, 1));
      endDate = formatDateToString(new Date(yearValue, monthValue, 0));
    } else {
      startDate = "";
      endDate = "";
    }
    return {
      startDate,
      endDate,
    };
  };

  return (
    <Box className="reportsContainer">
      <Box className="reportsContainer__search">
        <Box className="reportsContainer__search__searchbar">
          <Box>
            <Search className="header__searching__search-bar__search-icon" />
          </Box>
          <Autocomplete
            freeSolo
            forcePopupIcon={false}
            fullWidth
            options={unitNames}
            value={searchInput}
            inputValue={searchInput ?? ""}
            onChange={(e, newVal: any) => {
              setSearchInput(newVal);
            }}
            onInputChange={(e, newInput, reason) => {
              if (reason === "clear") {
                setSearchInput("");
              } else {
                setSearchInput(newInput);
              }
            }}
            renderInput={(params) => (
              <TextField
                value={searchInput}
                placeholder={"Buscar"}
                {...params}
              />
            )}
          />
        </Box>
      </Box>
      <Box className="reportsContainer__content">
        <Box className="reportsContainer__content__metrics">
          {metrics.map((metric: any, index: number) => (
            <MetricCard key={index} metric={metric} />
          ))}
        </Box>
        <Box className="reportsContainer__content__unitCounter">
          <Box className="reportsContainer__content__unitCounter__header">
            <Box className="reportsContainer__content__unitCounter__header__iconBox">
              <Lightbulb />
            </Box>
            <Box className="reportsContainer__content__unitCounter__header__label">
              Ideas por Unidad
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box className="reportsContainer__content__unitCounter__header__controls">
              <Select
                className="reportsContainer__content__unitCounter__header__controls__month"
                id="unitCounter-monthFilter"
                value={ideasCountMonthValue.toString()}
                IconComponent={(props) => <ChevronDown {...props} />}
                onChange={(event: SelectChangeEvent) =>
                  setIdeasCountMonthValue(parseInt(event?.target.value))
                }
                label="Mes"
              >
                <MenuItem value={"0"}>Seleccionar mes</MenuItem>
                {monthArray.map((month, index) => (
                  <MenuItem key={index + 1} value={month.id}>
                    {month.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                className="reportsContainer__content__unitCounter__header__controls__year"
                IconComponent={(props) => <ChevronDown {...props} />}
                id="unitCounter-yearFilter"
                value={ideasCountYearValue.toString()}
                onChange={(event: SelectChangeEvent) =>
                  setIdeasCountYearValue(parseInt(event?.target.value))
                }
                label="Año"
              >
                <MenuItem value={"0"}>Seleccionar año</MenuItem>
                {ideasYears.map((year: any, index: number) => (
                  <MenuItem key={index + 1} value={year.id.toString()}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          <DataTable
            rows={visibleCount}
            headers={ideasCountHeaders}
            isLoading={isLoadingData}
            tableType="checkbox"
            showCheckbox={false}
          />
        </Box>
      </Box>
    </Box>
  );
}
